import React from 'react';
import { motion } from 'framer-motion';
import { IconType } from 'react-icons';
import DynamicCard from './dynamic/DynamicCard';
import DynamicTag from './dynamic/DynamicTag';

interface SkillCardProps {
  skill: string;
  icon?: IconType;
  description: string;
  technologies: string[];
  proficiency?: number;
  category?: string;
  index?: number;
}

const SkillCard: React.FC<SkillCardProps> = ({ 
  skill, 
  icon: Icon, 
  description, 
  technologies,
  proficiency,
  category,
  index = 0
}) => {
  return (
    <DynamicCard delay={index * 0.1} className="h-full flex flex-col overflow-hidden relative">
      {/* Hover gradient overlay */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-br from-primary-500/0 to-blue-500/0 group-hover:from-primary-500/10 group-hover:to-blue-500/10 transition-all duration-500 pointer-events-none"
        initial={false}
      />
      
      <div className="flex items-center mb-4 relative z-10">
        {Icon && (
          <motion.div 
            className="text-primary-600 dark:text-primary-400 mr-4 flex-shrink-0"
            whileHover={{ 
              scale: 1.2, 
              rotate: [0, -10, 10, -10, 0],
              transition: { duration: 0.5 }
            }}
          >
            <Icon size={32} />
          </motion.div>
        )}
        <div className="flex-grow">
          <motion.h3 
            className="text-2xl font-bold text-gray-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-300"
            whileHover={{ x: 5 }}
            transition={{ duration: 0.2 }}
          >
            {skill}
          </motion.h3>
          {category && (
            <motion.span 
              className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide"
              initial={{ opacity: 0.7 }}
              whileHover={{ opacity: 1 }}
            >
              {category}
            </motion.span>
          )}
        </div>
      </div>
      
      <motion.p 
        className="text-gray-600 dark:text-gray-300 mb-4 flex-grow relative z-10"
        initial={{ opacity: 0.8 }}
        whileHover={{ opacity: 1 }}
        transition={{ duration: 0.2 }}
      >
        {description}
      </motion.p>
      
      {proficiency !== undefined && (
        <motion.div 
          className="mb-4 relative z-10"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
        >
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Proficiency</span>
            <motion.span 
              className="text-sm font-semibold text-primary-600 dark:text-primary-400"
              initial={{ scale: 0.8 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4, type: "spring" }}
            >
              {proficiency}%
            </motion.span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 overflow-hidden">
            <motion.div
              className="bg-gradient-to-r from-primary-600 to-primary-400 dark:from-primary-500 dark:to-primary-300 h-2.5 rounded-full"
              initial={{ width: 0 }}
              whileInView={{ width: `${proficiency}%` }}
              viewport={{ once: true }}
              transition={{ delay: 0.5, duration: 1, ease: "easeOut" }}
            />
          </div>
        </motion.div>
      )}
      
      {technologies && technologies.length > 0 && (
        <motion.div 
          className="flex flex-wrap gap-2 pt-4 border-t border-gray-200 dark:border-gray-700 relative z-10"
          initial={{ opacity: 0.7 }}
          whileHover={{ opacity: 1 }}
        >
          {technologies.map((tech, techIndex) => (
            <motion.div
              key={techIndex}
              whileHover={{ scale: 1.1, y: -2 }}
              transition={{ type: "spring", stiffness: 400 }}
            >
              <DynamicTag variant="primary" size="sm">
                {tech}
              </DynamicTag>
            </motion.div>
          ))}
        </motion.div>
      )}
    </DynamicCard>
  );
};

export default SkillCard;
